// Lightweight YouTube playlist helper for client-side usage
// Fetches the most recent video from a public playlist feed without API keys

const DEFAULT_CACHE_DURATION_MS = 15 * 60 * 1000; // 15 minutes

const getCacheKeyForPlaylist = (playlistId) => `yt_playlist_${playlistId}_latest`;

const readFromCache = (playlistId, cacheDurationMs = DEFAULT_CACHE_DURATION_MS) => {
  try {
    const raw = localStorage.getItem(getCacheKeyForPlaylist(playlistId));
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp < cacheDurationMs) {
      return data;
    }
  } catch (_) {}
  return null;
};

const writeToCache = (playlistId, data) => {
  try {
    localStorage.setItem(
      getCacheKeyForPlaylist(playlistId),
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch (_) {}
};

// Kept for potential future use if backend response changes
// eslint-disable-next-line no-unused-vars
const parseLatestFromPlaylistXml = (xmlText) => {
  try {
    // Try DOMParser first
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlText, 'application/xml');
    const entry = doc.getElementsByTagName('entry')[0];
    if (entry) {
      // Namespaced tags need escaping in querySelector
      const videoIdEl = entry.querySelector('yt\\:videoId');
      const titleEl = entry.getElementsByTagName('title')[0];
      if (videoIdEl && titleEl) {
        return { videoId: videoIdEl.textContent, title: titleEl.textContent };
      }
    }
  } catch (_) {}

  // Fallback: regex parse for robustness
  try {
    const idMatch = xmlText.match(/<yt:videoId>(.*?)<\/yt:videoId>/);
    const titleMatch = xmlText.match(/<title>(.*?)<\/title>/);
    if (idMatch && idMatch[1]) {
      return { videoId: idMatch[1], title: titleMatch ? titleMatch[1] : '' };
    }
  } catch (_) {}

  return null;
};

export const fetchLatestPlaylistVideo = async (
  playlistId,
  { forceRefresh = false, cacheDurationMs = DEFAULT_CACHE_DURATION_MS } = {}
) => {
  if (!playlistId) return null;

  if (!forceRefresh) {
    const cached = readFromCache(playlistId, cacheDurationMs);
    if (cached) return cached;
  }

  // Always use backend proxy to avoid CORS issues
  const proxyResp = await fetch(`/api/youtube/latest?playlistId=${encodeURIComponent(playlistId)}`, { cache: 'no-store' });
  if (proxyResp.ok) {
    const latest = await proxyResp.json();
    if (latest && latest.videoId) {
      writeToCache(playlistId, latest);
      return latest;
    }
  }

  return null;
};

export const clearPlaylistCache = (playlistId) => {
  try {
    localStorage.removeItem(getCacheKeyForPlaylist(playlistId));
  } catch (_) {}
};


